import React, { ChangeEvent, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
} from "@mui/material";
import Close from "@mui/icons-material/Close";
import { useAppContext } from "../../../../context/AppContext";
import { DISABLE_RTL_PROPS, getDisableRTLStyles } from "../../../../utils/rtlUtils";
import { COMMON_MUI_STYLES } from "../../../../styles";

interface EmailVerificationModalProps {
  open: boolean;
  onClose: () => void;
  onVerify: () => void;
  onResend: () => void;
  codeDigits: string[];
  onCodeInput: (e: ChangeEvent<HTMLInputElement>, idx: number) => void;
  onCodeKeyDown: (
    e: React.KeyboardEvent<HTMLInputElement>,
    idx: number
  ) => void;
  onCodePaste: (e: React.ClipboardEvent<HTMLInputElement>) => void;
  resendLoading: boolean;
  resendCountdown: number;
  isVerifyLoading: boolean;
}

export const EmailVerificationModal: React.FC<EmailVerificationModalProps> = ({
  open,
  onClose,
  onVerify,
  onResend,
  codeDigits,
  onCodeInput,
  onCodeKeyDown,
  onCodePaste,
  resendLoading,
  resendCountdown,
  isVerifyLoading,
}) => {
  const { getTranslation } = useAppContext();
  const firstInputRef = useRef<HTMLInputElement>(null);

  // Auto-focus first input when modal opens
  useEffect(() => {
    if (open && !resendLoading) {
      // Small delay to ensure modal is fully rendered
      const timer = setTimeout(() => {
        firstInputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [open, resendLoading]);

  return (
    <Dialog
      open={open}
      BackdropProps={{
        style: { backgroundColor: "rgba(255,255,255,0.0)" },
      }}
      onClose={(_, reason) => {
        if (reason !== "backdropClick") {
          onClose();
        }
      }}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
          p: 0,
        },
      }}
    >
      <DialogTitle
        sx={{
          textAlign: "center",
          fontWeight: 700,
          fontSize: "1.5rem",
          color: "var(--color-text-primary)",
          pt: 3,
          pb: 1,
          position: "relative",
        }}
      >
        {getTranslation("Txt20")}
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "var(--color-text-secondary)",
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 4, pb: 2 }}>
        {resendLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 131,
            }}
          >
            <CircularProgress sx={{ color: "var(--color-text-primary)" }} />
          </Box>
        ) : (
          <>
            <Typography
              variant="body2"
              sx={{
                mb: 2,
                textAlign: "center",
                color: "var(--color-text-primary)",
                fontSize: { xs: "0.8rem", sm: "1.3rem", md: "1.2rem" },
                lineHeight: 1.5,
              }}
            >
              {getTranslation("Txt836")}
            </Typography>
            <Box
              {...DISABLE_RTL_PROPS}
              sx={{ 
                display: "flex", 
                justifyContent: "center", 
                alignItems: "center",
                gap: { xs: 1, sm: 1.5, md: 2 }, 
                mb: 2,
                flexWrap: "wrap",
                px: { xs: 1, sm: 2 },
                width: "100%",
                ...getDisableRTLStyles({ textAlign: "center" }).sx,
              }}
            >
              {codeDigits.map((digit, idx) => {
                const otpStyles = getDisableRTLStyles({ textAlign: "center" });
                return (
                <input
                  key={idx}
                  ref={idx === 0 ? firstInputRef : undefined}
                  id={`code-input-${idx}`}
                    className={`${idx === 0 && !digit ? "otp-input-blinking" : ""} ${otpStyles.className}`}
                    {...otpStyles.props}
                  value={digit}
                  onChange={(e) => onCodeInput(e, idx)}
                  onKeyDown={(e) => onCodeKeyDown(e, idx)}
                  onPaste={onCodePaste}
                  onFocus={(e) => {
                    e.target.classList.add("otp-input-focused");
                  }}
                  onBlur={(e) => {
                    e.target.classList.remove("otp-input-focused");
                  }}
                  maxLength={1}
                  style={{
                    width: "3rem",
                    height: "3.5rem",
                    fontSize: "1.5rem",
                    textAlign: "center",
                    border: "none",
                    borderBottom: "2px solid #000",
                    background: "#fff",
                    outline: "none",
                    marginRight: idx !== 3 ? "0.5rem" : 0,
                      marginLeft: 0,
                    fontWeight: 600,
                    letterSpacing: "0.1em",
                    padding: "0.5rem 0",
                    lineHeight: "1.2",
                    WebkitAppearance: "none",
                    borderRadius: "0",
                    minHeight: "3.5rem",
                    transition: "border-bottom-color 0.1s ease, border-bottom-width 0.1s ease",
                      direction: "ltr",
                  }}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete="one-time-code"
                />
                );
              })}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          flexDirection: "column",
          gap: 2,
          px: 4,
          pb: 4,
          pt: 0,
        }}
      >
        <Button
          onClick={onVerify}
          variant="contained"
          disabled={isVerifyLoading}
          sx={{
            ...COMMON_MUI_STYLES.primaryCtaButton1,
            position: 'relative',
          }}
        >
          {isVerifyLoading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={20} sx={{ color: "var(--color-text-inverse)" }} />
            </Box>
          ) : (
            getTranslation("Txt837")
          )}
        </Button>
        <Button
          onClick={onResend}
          variant="contained"
          fullWidth
          disabled={resendCountdown > 0}
          sx={{
            ...COMMON_MUI_STYLES.button,
            ...(resendCountdown > 0 && {
              backgroundColor: "#cccccc",
              color: "var(--color-text-secondary)",
              "&:hover": {
                backgroundColor: "#cccccc",
              },
            }),
          }}
        >
          {resendCountdown > 0
            ? `${getTranslation("Txt838")} (${Math.floor(
                resendCountdown / 60
              )}:${(resendCountdown % 60).toString().padStart(2, "0")})`
            : getTranslation("Txt838")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
